import type { EntryChanges } from '@realmery/realm-core';

/**
 * Autosave for one entry (docs/ENTRY-EDITOR-PLAN.md section 4.4), framework-free so it
 * can be tested with fake timers. Changes are merged and written 1.5 seconds after the
 * last one, or at once on `flush` (leaving the entry, hiding or closing the page). One
 * write runs at a time; changes made during it go into the next. A failed write keeps its
 * changes and is tried again, unless the failure is one a retry cannot fix.
 */

export type SaveState = 'saved' | 'pending' | 'saving' | 'error';

export interface SaveStatus {
  readonly state: SaveState;
  /** Why the last write failed, for the status line. */
  readonly error?: string;
}

/** A failure a retry cannot fix: the entry is gone, locked, or the change is invalid. */
export class FinalSaveError extends Error {}

export interface EntrySaverOptions<C extends object = EntryChanges> {
  readonly write: (changes: C) => Promise<void>;
  readonly onStatus: (status: SaveStatus) => void;
  /** Tells a failure a retry cannot fix from one it may (storage busy, a lost worker). */
  readonly isFinal?: (error: unknown) => boolean;
  readonly delayMs?: number;
  readonly retryMs?: number;
}

export const SAVE_DELAY_MS = 1500;
export const RETRY_MS = 5000;

/** Story Studio's scenes are saved the same way, with their own changes (`C`). */
export class EntrySaver<C extends object = EntryChanges> {
  #pending: C | null = null;
  #writing: Promise<void> | null = null;
  #timer: ReturnType<typeof setTimeout> | undefined;
  #status: SaveStatus = { state: 'saved' };
  #disposed = false;
  readonly #options: EntrySaverOptions<C>;

  constructor(options: EntrySaverOptions<C>) {
    this.#options = options;
  }

  get status(): SaveStatus {
    return this.#status;
  }

  /** Whether anything is not yet sealed into the realm: waiting, being written, or failed. */
  get unsaved(): boolean {
    return this.#pending !== null || this.#writing !== null;
  }

  /** Records a change, written after the delay unless more follow. */
  change(changes: C): void {
    if (this.#disposed) throw new Error('This entry is no longer being edited.');
    this.#pending = { ...this.#pending, ...changes } as C;
    if (this.#status.state !== 'saving') this.#setStatus({ state: 'pending' });
    this.#schedule(this.#options.delayMs ?? SAVE_DELAY_MS);
  }

  /** Writes everything now, and resolves once it is written or has failed. */
  async flush(): Promise<void> {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    while (this.#writing) await this.#writing;
    if (this.#pending) await this.#write();
  }

  /** Writes what is left, then stops: for when the entry is closed. */
  async dispose(): Promise<void> {
    await this.flush();
    this.#disposed = true;
    clearTimeout(this.#timer);
  }

  #schedule(ms: number): void {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      void this.flush();
    }, ms);
  }

  #write(): Promise<void> {
    const changes = this.#pending!;
    this.#pending = null;
    this.#setStatus({ state: 'saving' });
    const writing = this.#options
      .write(changes)
      .then(() => {
        if (this.#pending) {
          this.#setStatus({ state: 'pending' });
        } else {
          this.#setStatus({ state: 'saved' });
        }
      })
      .catch((error: unknown) => {
        // Keep the failed changes, under any made since.
        this.#pending = { ...changes, ...this.#pending } as C;
        this.#setStatus({ state: 'error', error: error instanceof Error ? error.message : 'The entry could not be saved.' });
        const final = error instanceof FinalSaveError || (this.#options.isFinal?.(error) ?? false);
        if (!final && !this.#disposed) this.#schedule(this.#options.retryMs ?? RETRY_MS);
      })
      .finally(() => {
        this.#writing = null;
      });
    this.#writing = writing;
    return writing;
  }

  #setStatus(status: SaveStatus): void {
    this.#status = status;
    this.#options.onStatus(status);
  }
}
